import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import {
  FiKey,
  FiActivity,
  FiTerminal,
  FiPieChart,
  FiUsers,
  FiSettings,
  FiArrowRight,
  FiRefreshCw
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getPlatformOverview } from '../../services/monitorService';
import axios from 'axios';

// 仪表板统计数据(授权码维度)
const fetchDashboardStats = async () => {
  const response = await axios.get('/api/dashboard/stats');
  return response.data;
};

// 最近活动
const fetchRecentActivities = async () => {
  const response = await axios.get('/api/dashboard/activities');
  return response.data;
};

// 金额格式化(人民币, 千分位)
const fmtMoney = (v) => {
  const n = Number(v) || 0;
  return '¥' + n.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
};

// 涨红跌绿(国内金融惯例): 盈利红色, 亏损绿色
const profitClass = (v) => {
  const n = Number(v) || 0;
  if (n > 0) return 'text-[#cf222e]';
  if (n < 0) return 'text-[#1a7f37]';
  return 'text-[#656d76]';
};

const fmtTime = (t) => {
  if (!t) return '-';
  const d = new Date(t);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 顶部指标卡
const MetricCard = ({ title, value, sub, valueClass = 'text-[#1f2328]', icon, iconBg, iconColor }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="bg-white rounded-md py-5 px-6 relative overflow-hidden shadow-sm border border-[#d0d7de]"
  >
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-[#656d76] text-sm mb-1">{title}</h3>
        <div className={`text-xl md:text-2xl font-semibold ${valueClass}`}>
          {value}
          {sub && <span className="text-sm font-normal text-[#656d76] ml-1">{sub}</span>}
        </div>
      </div>
      <div className="text-xl p-2.5 rounded-md shrink-0" style={{ backgroundColor: iconBg, color: iconColor }}>
        {icon}
      </div>
    </div>
  </motion.div>
);

// 板块入口卡
const SectionCard = ({ to, title, badge, desc, metric, icon, iconBg, iconColor }) => (
  <Link
    to={to}
    className="group bg-white rounded-lg border border-[#d0d7de] shadow-sm p-5 hover:border-[#8b949e] hover:shadow transition-all flex flex-col"
  >
    <div className="flex items-center gap-2 mb-2">
      <span className="text-lg p-2 rounded-md" style={{ backgroundColor: iconBg, color: iconColor }}>
        {icon}
      </span>
      <span className="text-[#1f2328] font-medium">{title}</span>
      {badge && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ddf4ff] text-[#0969da] border border-[#b6e3ff]">
          {badge}
        </span>
      )}
    </div>
    <p className="text-[#656d76] text-xs mb-3">{desc}</p>
    <div className="mt-auto flex items-center justify-between">
      <span className="text-[#1f2328] text-sm">{metric}</span>
      <span className="text-[#0969da] text-xs flex items-center gap-1 opacity-70 group-hover:opacity-100">
        进入 <FiArrowRight />
      </span>
    </div>
  </Link>
);

const Dashboard = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError
  } = useQuery('platformOverview', getPlatformOverview, { refetchInterval: 30000 });

  const {
    data: statsData,
    isLoading: statsLoading
  } = useQuery('dashboardStats', fetchDashboardStats);

  const {
    data: activitiesData,
    isLoading: activitiesLoading
  } = useQuery('recentActivities', fetchRecentActivities);

  const overview = overviewData?.overview || {};
  const stats = statsData?.stats || {};
  const activities = activitiesData?.activities || [];

  if ((overviewLoading || statsLoading) && !overviewError) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-40" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="skeleton h-28" />
          <div className="skeleton h-28" />
          <div className="skeleton h-28" />
          <div className="skeleton h-28" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="skeleton h-32" />)}
        </div>
      </div>
    );
  }

  const dayProfit = Number(overview.dayProfit) || 0;

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">平台总览</h1>
          <p className="text-[#656d76] text-sm mt-1">
            各板块运行状态一目了然，点击卡片进入对应模块
            {isAdmin && <span className="ml-2 text-[#0969da]">（超管视角：含全系统数据）</span>}
          </p>
        </div>
        <span className="hidden md:flex items-center gap-1 text-xs text-[#656d76]">
          <FiRefreshCw /> 30 秒自动刷新
        </span>
      </div>

      {/* 关键指标 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="在线账户"
          value={overview.onlineInstances ?? 0}
          sub={`/ ${overview.totalInstances ?? 0}`}
          icon={<FiActivity />}
          iconBg="#dafbe1"
          iconColor="#1a7f37"
        />
        <MetricCard
          title="账户总净值"
          value={fmtMoney(overview.totalEquity)}
          icon={<FiPieChart />}
          iconBg="#ddf4ff"
          iconColor="#0969da"
        />
        <MetricCard
          title="当日盈亏"
          value={(dayProfit > 0 ? '+' : '') + fmtMoney(dayProfit)}
          valueClass={profitClass(dayProfit)}
          icon={<FiRefreshCw />}
          iconBg="#ffebe9"
          iconColor="#cf222e"
        />
        <MetricCard
          title="待执行指令"
          value={overview.pendingCommands ?? 0}
          valueClass={(overview.pendingCommands ?? 0) > 0 ? 'text-[#9a6700]' : 'text-[#1f2328]'}
          icon={<FiTerminal />}
          iconBg="#fff8c5"
          iconColor="#9a6700"
        />
      </div>

      {/* 板块入口 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SectionCard
          to="/statistics"
          title="数据统计"
          badge="新"
          desc="历史曲线、盈亏报表导出"
          metric="按实例查看净值/盈亏曲线"
          icon={<FiPieChart />}
          iconBg="#fbefff"
          iconColor="#8250df"
        />
        <SectionCard
          to="/monitor/instances"
          title="实时监控"
          badge="新"
          desc="余额、净值、当日盈亏实时刷新"
          metric={`在线 ${overview.onlineInstances ?? 0} · 共 ${overview.totalInstances ?? 0} 账户`}
          icon={<FiActivity />}
          iconBg="#dafbe1"
          iconColor="#1a7f37"
        />
        <SectionCard
          to="/commands"
          title="远程指令"
          badge="新"
          desc="一键平仓、一键暂停/恢复"
          metric={`排队 ${overview.pendingCommands ?? 0} 条`}
          icon={<FiTerminal />}
          iconBg="#ffebe9"
          iconColor="#cf222e"
        />
        <SectionCard
          to="/applications"
          title="授权管理"
          desc="应用、授权码、申请审核"
          metric={`应用 ${stats.applications ?? 0} · 授权码 ${stats.totalLicenses ?? 0}`}
          icon={<FiKey />}
          iconBg="#ddf4ff"
          iconColor="#0969da"
        />
        {isAdmin && (
          <SectionCard
            to="/users"
            title="用户权限"
            desc="注册用户、角色管理"
            metric={`用户 ${statsData?.systemStats?.totalUsers ?? 0} · 超管 1`}
            icon={<FiUsers />}
            iconBg="#fff8c5"
            iconColor="#9a6700"
          />
        )}
        {isAdmin && (
          <SectionCard
            to="/settings"
            title="系统设置"
            desc="授权规则、通知配置"
            metric="授权码格式与应用ID"
            icon={<FiSettings />}
            iconBg="#f6f8fa"
            iconColor="#57606a"
          />
        )}
      </div>

      {/* 最近动态 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm">
        <div className="px-5 py-3 border-b border-[#d0d7de] flex items-center justify-between">
          <h2 className="text-[#1f2328] font-medium">最近动态</h2>
          <Link to="/licenses" className="text-[#0969da] text-xs flex items-center gap-1 hover:underline">
            查看全部 <FiArrowRight />
          </Link>
        </div>
        <div className="divide-y divide-[#eaeef2]">
          {activitiesLoading ? (
            <div className="px-5 py-8 text-center text-[#656d76] text-sm">加载中...</div>
          ) : activities.length === 0 ? (
            <div className="px-5 py-8 text-center text-[#656d76] text-sm">暂无动态</div>
          ) : (
            activities.slice(0, 8).map((a) => (
              <div key={a.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[#1f2328] font-medium truncate">{a.customerName || '—'}</span>
                  <span className="text-[#656d76] truncate hidden sm:inline font-mono">{a.account || '—'}</span>
                  <span className="text-[#656d76] truncate hidden md:inline">{a.applicationName}</span>
                  <span className="font-mono text-[#1f2328] truncate hidden lg:inline">{a.licenseKey}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    a.action === 'active' ? 'bg-[#dafbe1] text-[#1a7f37]' :
                    a.action === 'pending' ? 'bg-[#fff8c5] text-[#9a6700]' :
                    'bg-[#f6f8fa] text-[#57606a]'
                  }`}>
                    {a.action === 'active' ? '已激活' : a.action === 'pending' ? '待激活' : a.action}
                  </span>
                  <span className="text-[#656d76] text-xs">{fmtTime(a.timestamp)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
